/** @jsxImportSource @emotion/react */
import { Global } from "@emotion/react";
import {
  alpha,
  Box,
  Card,
  CardContent,
  Grid,
  ThemeProvider,
  Typography,
  typographyClasses,
  useMediaQuery,
} from "@mui/material";
import React from "react";
import Image, { FileResolver } from "../components/image";
import LinkArrowLong from "../components/LinkArrowLong";
import { LocalizedLink } from "../components/LocalizedLink";
import MabiloftSmoothScrollInterpolator from "../components/MabiloftSmoothScrollInterpolator";
import BubbleCircle from "../components/old/bubble_circle";
import SEO from "../components/old/seo";
import { useDimensions } from "../components/old/withDimensions";
import Footer from "../components/shared/Footer/Footer";
import MissingContent from "../components/shared/MissingContent/MissingContent";
import { MText, ress } from "../components/shared/mtext";
import PrevNextCaseStudy from "../components/shared/PrevNextCaseStudy/PrevNextCaseStudy";
import {
  CardsBlock,
  CtaBlock,
  IBlockType,
  ICaseStudyContent,
  ImageBlock,
  ParallaxBlock,
  RowBlock,
} from "../contents/case_studies";
import { filterCaseStudiesForLocale } from "../contents/case_studies/localCaseStudies";
import theme, { darkTheme } from "../layouts/theme";
import { isSafari } from "../utils";

function getImagePath(path: string, pageContext: any) {
  return `case_studies/${pageContext.slug}/${path}`;
}

export const CircleCover = (props: {
  locale: string;
  backgroundImage: string;
}) => {
  return (
    <Image filename={props.backgroundImage}>
      {(image) => {
        const imgpath = image?.node?.childImageSharp?.fluid?.src;
        console.log(imgpath);
        return (
          <BubbleCircle
            id={"bg-img-" + props.locale}
            backgroundImage={imgpath}
            // backgroundImage="https://www.naturopataonline.org/wp-content/uploads/2016/12/verde-2-770x515.jpg"
          />
        );
      }}
    </Image>
  );
};

const WorkCard = (props: {
  work: ICaseStudyContent;
  index: number;
  totalCount: number;
  textColor?: string;
  isBottom?: boolean;
  [x: string]: any;
}) => {
  const { work, index, totalCount, ...otherProps } = props;

  const mobile = useMediaQuery("(max-width: 899px)");

  return (
    // <StyledEngineProvider injectFirst>
    <ThemeProvider theme={mobile ? theme : darkTheme}>
      {mobile && (
        <React.Fragment>
          <Grid
            container
            alignItems="center"
            css={{
              marginBottom: 24,
              color: props.textColor ? props.textColor : "black",
            }}
          >
            <Grid item xs>
              <MText
                children={work.title}
                variant="h4"
                color={props.textColor ? "inherit" : "primary"}
              />
              <MText
                children={work.previewCaption}
                variant="body1"
                color={props.textColor ? "inherit" : "primary"}
                opacity={0.6}
              />
            </Grid>
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              css={{
                width: "auto",
                // width: 110,
                // height: 110,
                borderRadius: 110,
                border: "2px solid #E85322",
                padding: 12,
              }}
            >
              <MText
                children={`• VOL ${index + 1}/${totalCount}`}
                variant="body1"
                color="secondary"
              />
            </Grid>
          </Grid>
        </React.Fragment>
      )}

      {props.isBottom && !mobile && (
        <div
          css={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0px 96px",
            boxSizing: "border-box",
            paddingBottom: 48,
          }}
        >
          <Grid
            container
            alignItems="center"
            css={{
              marginBottom: 24,
              color: props.textColor ? props.textColor : "black",
            }}
          >
            <Grid item xs>
              <MText
                children={work.title}
                variant="h4"
                color={props.textColor ? "inherit" : "primary"}
              />
              <MText
                children={work.previewCaption}
                variant="subtitle1"
                color={props.textColor ? "inherit" : "primary"}
                css={{ fontWeight: 400, opacity: 0.6 }}
              />
            </Grid>
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              css={{
                width: 110,
                height: 110,
                borderRadius: 110,
                border: "2px solid #E85322",
                padding: 12,
              }}
            >
              <MText
                children={`• VOL ${index + 1}/${totalCount}`}
                variant="body1"
                color="secondary"
              />
            </Grid>
          </Grid>
        </div>
      )}

      <Card
        css={{
          borderRadius: 96,
          minHeight: props.isBottom ? 705 : 800,
          position: "relative",
          ...ress("lg", {
            minHeight: 640,
          }),
          ...ress("sm", {
            minHeight: 480,
            borderRadius: 24,
          }),
          ...ress("xs", {
            minHeight: 336,
            borderRadius: 18,
          }),
        }}
        {...otherProps}
      >
        {!mobile && !props.isBottom && (
          <CardContent
            css={{
              position: "relative",
              zIndex: 3,
              padding: 48,
              "&:last-child": { paddingBottom: 48 },
            }}
          >
            <Grid container alignItems="center">
              <Grid item xs>
                <MText children={work.title} variant="h4" color="primary" />
                <MText
                  children={work.previewTitle}
                  variant="body1"
                  color="primary"
                  opacity={0.6}
                  css={{
                    marginTop: 12,
                    textTransform: "uppercase",
                  }}
                />
              </Grid>
              {index !== null && index !== undefined && (
                <Grid item>
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    css={{
                      width: 110,
                      height: 110,
                      borderRadius: 110,
                      border: "2px solid #ffffff",
                    }}
                  >
                    <MText
                      children={`• VOL ${index + 1}/${totalCount}`}
                      variant="body1"
                      color="primary"
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </CardContent>
        )}
        <div
          css={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 1,
          }}
        >
          {!mobile && (
            <div
              css={{
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: 2,
                background:
                  !mobile && !props.isBottom
                    ? "linear-gradient(180deg, rgba(27, 32, 60, 0.6) 0%, rgba(27, 32, 60, 0) 100%);"
                    : "unset",
              }}
            />
          )}
          <Image
            // filename={`case_studies/${(work as any).slug}/${
            //   work.circleCover.backgroundImage
            // }`}
            filename={`case_studies/${(work as any).slug}/${work.previewImage}`}
            imageProps={{
              css: {
                width: "100%",
                height: "100%",
                zIndex: 1,
              },
            }}
          />
        </div>
      </Card>
    </ThemeProvider>
    // </StyledEngineProvider>
  );
};

const Header: React.FC<{ pageContext: any; content: ICaseStudyContent }> = ({
  pageContext,
  content,
}) => {
  return (
    <Grid
      key={content.title}
      container
      direction="row"
      justifyContent="center"
      css={{
        marginTop: 120,
        whiteSpace: "nowrap",
        "@media screen and (max-width: 1279.9px)": {
          marginTop: 60,
          whiteSpace: "pre-line",
          flexDirection: "column-reverse",
          textAlign: "center",
          alignItems: "center",
          ".btn-long-arrow": {
            paddingLeft: 72,
          },
        },
      }}
    >
      <div
        css={{
          width: 720,
          height: 484,
          //0.67%
          position: "relative",
          "@media screen and (max-width: 1279.9px)": {
            alignSelf: "center",
            "@media screen and (max-width: 599.9px)": {
              // width: 342,
              // height: 230
              width: "90vw",
              height: "60vw",
            },
          },
        }}
      >
        <div
          css={{
            width: 484,
            height: "100%",
            position: "relative",
            "@media screen and (max-width: 599.9px)": {
              // width: 230
              width: "60vw",
            },
          }}
        >
          <CircleCover
            locale={pageContext.locale}
            backgroundImage={getImagePath(
              content.circleCover.backgroundImage,
              pageContext
            )}
          />
        </div>

        <Image
          filename={getImagePath(content.circleCover.raisedImage, pageContext)}
          imageProps={{
            css: {
              position: "absolute !important",
              // top: 0,
              // left: 0,
              top: 7.5,
              left: -10,
              width: "100%",
              height: "100%",
            },
          }}
        />
      </div>

      <Grid
        container
        item
        // md={12}
        // justify="center"
        alignItems="center"
        css={{
          whiteSpace: "pre-line",
          flex: 1,
          maxWidth: 600,
        }}
      >
        <div
          css={{
            marginLeft: 48,
            marginBottom: 24,
            maxWidth: 480,
            "@media screen and (max-width: 1279.9px)": {
              marginLeft: 0,
              maxWidth: "none",
              flex: 1,
            },
          }}
        >
          <h2
            dangerouslySetInnerHTML={{ __html: content.title }}
            css={{
              fontSize: 52,
              margin: 0,
              marginBottom: 24,
              whiteSpace: "pre-line",
              "@media screen and (max-width: 599.9px)": {
                fontSize: 37,
              },
            }}
          />

          <b
            dangerouslySetInnerHTML={{ __html: content.subtitle }}
            css={{
              display: "block",
              whiteSpace: "pre-line",
              fontSize: 22,
              margin: 0,
              "@media screen and (max-width: 599.9px)": {
                fontSize: 17,
              },
            }}
          />

          {content.link && (
            <LinkArrowLong
              target="_blank"
              href={content.link.href}
              className="btn-long-arrow"
              children={content.link.content}
            />
          )}
        </div>
      </Grid>
    </Grid>
  );
};

const CaseStudy = (props: any) => {
  //build
  let content = {} as ICaseStudyContent;
  // const content = props.pageContext.locale === "en" ? content_en : content_it;

  const works = filterCaseStudiesForLocale(props.pageContext.locale);

  try {
    // const filepath = `../contents/case_studies/${props.pageContext.slug}/${props.pageContext.locale}`;

    const fileId: string = "content_" + props.pageContext.locale;
    const fileString: string = props.pageContext[fileId];
    /*
    const firstBracketIndex = fileString.indexOf("{");
    const secondBracketIndex = fileString.indexOf("{", firstBracketIndex + 1);
    const lastBracketIndex = fileString.lastIndexOf("}") + 1;
    //
    const fileJsonBody = fileString.substring(
      secondBracketIndex,
      lastBracketIndex
    );

    console.log(5552, fileId, fileJsonBody);

    const fileJsonClean = (fileJsonBody as any)
      .replaceAll("\n", "")
      .replaceAll("\r", "")
      .replaceAll("`", '"');
    content = JSON.parse(fileJsonClean);
    */
    content = JSON.parse(fileString);
  } catch (exception) {
    // console.log(555, "❌ err parsing", exception);
  }

  const notExists = Object.keys(content as any).length === 0; //props.pageContext.locale !== "it"; //!last
  // const notExists = false; // !article.body || (!article.isVisible && process.env.NODE_ENV === "production")

  if (notExists) {
    return (
      <React.Fragment>
        <SEO noIndex={true} />
        <MissingContent />
        <Footer />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Header pageContext={props.pageContext} content={content} />

      <SEO
        title={content.title}
        description={(
          content.seoDescription ||
          content.previewTitle ||
          content.subtitle ||
          ""
        ).replace(/<\/?[^>]+(>|$)/g, "")}
        // image={"/img/startup-geeks.png"}
        image={"/case_studies_png/" + props.pageContext.slug + ".png"}
        // image={getImagePath(content.seoImage)}
      />

      <div
        css={{
          // boxShadow: "0 0 10px  red",
          color: "#1B203C",
        }}
      >
        <Grid
          container
          direction="column"
          alignItems="center"
          css={{
            flexWrap: "nowrap",
            marginTop: 120,
            [`& .${typographyClasses.root}`]: {
              marginBottom: 24,
            },
            "> *": {
              marginBottom: 24,
              width: "100%",
            },
            "@media screen and (max-width: 959.9px)": {
              marginTop: 48,
            },
            ...ress("sm", {
              padding: "0px 20px",
            }),
          }}
        >
          {renderBlockChildrens(content.blocks)}
        </Grid>

        <Global
          styles={{
            ".casing": {
              //border: "1px solid red",
              paddingBottom: 0,
            },
            "@media screen and (max-width: 959.9px)": {
              "footer.casing": {
                marginTop: 0,
              },
            },
          }}
        />

        {content.endImage && (
          <MabiloftSmoothScrollInterpolator
            id="end"
            duration={0.75}
            tweens={[
              {
                selector: " .img-center",
                from: { y: 100 },
                to: { y: 0 },
              },
              {
                selector: " .img-side",
                from: { y: 200 },
                to: { y: 0 },
              },
            ]}
          >
            <div
              css={{
                position: "relative",
                width: "100%",
                // marginTop: "15%",
                transform: `scale(${content.endImage.scale || 1.5})`,

                // height: 20,
                // backgroundColor: "orange",
                // marginBottom: -32,

                "@media screen and (max-width: 599.9px)": {
                  // marginBottom: -(40 + 32)
                },
              }}
            >
              <Image
                filename={getImagePath(content.endImage.side)}
                imageProps={{
                  className: "img-side",
                  css: {
                    position: "absolute !important",
                    top: 0,
                    left: 0,
                    width: "100%",
                  },
                }}
              />
              <Image
                filename={getImagePath(content.endImage.center)}
                imageProps={{
                  className: "img-center",
                  css: { width: "100%" },
                }}
              />
            </div>
          </MabiloftSmoothScrollInterpolator>
        )}
      </div>

      <Box sx={{ paddingTop: { md: "300px", xs: "100px" } }}></Box>
      <PrevNextCaseStudy />
      <Footer />
    </React.Fragment>
  );

  function renderNextArticle() {
    const current = works.findIndex((elem) => elem.title === content.title);
    let index = 0;
    if (current !== works.length - 1) {
      index = current + 1;
    }
    const work = works[index];

    return (
      <div css={{ padding: "20px 0px" }}>
        <LocalizedLink withoutPreload={isSafari} to={`/works/${work.slug}/`}>
          <WorkCard
            work={work}
            index={index}
            totalCount={works.length}
            textColor="white"
            isBottom
            css={{
              marginBottom: 192,
              ...ress("md", { marginBottom: 48 }),
              ...ress("sm", { marginBottom: 24 }),
            }}
          />
        </LocalizedLink>
      </div>
    );
  }

  function getImagePath(path: string) {
    return `case_studies/${props.pageContext.slug}/${path}`;
  }

  function renderBlockChildrens(blocks: (IBlockType | RowBlock)[]) {
    return blocks.map((b, i) => (
      <React.Fragment key={i} children={renderBlock(b, i)} />
    ));
  }

  function renderBlock(block: IBlockType | RowBlock, index: number) {
    //render blocks
    switch (block.block) {
      case "h1":
      case "h2":
      case "h3":
      case "h4":
      case "h5":
      case "h6":
      case "body1":
        const isHeading = block.block[0] === "h";
        return (
          <Typography
            variant={block.block}
            // children={block.content}
            children={
              <span
                dangerouslySetInnerHTML={{
                  __html: block.content,
                }}
              />
            }
            css={{
              whiteSpace: "pre-line",
              fontFamily: "Nunito Sans",
              width: "100%",
              maxWidth: block.maxWidth === false ? undefined : 570,
              textAlign: block.align,
              fontWeight: isHeading ? "bold" : 400,
              ...(isHeading
                ? {
                    marginTop: 72,
                    color: "rgba(56,61,85,0.43)",
                    " b": { color: "#1B203C" },
                    "@media screen and (max-width: 599.9px)": {
                      fontSize: 26, //TODO - adapt to each header
                      marginTop: 24,
                    },
                  }
                : {}),
            }}
          />
        );
      case "video":
        const vidpath = getImagePath(block.content);
        return (
          <FileResolver filename={vidpath}>
            {(file) => {
              return (
                <video
                  autoPlay={true}
                  playsInline={true}
                  muted={true}
                  loop={true}
                  css={{
                    width: "100%",
                    pointerEvents: "none",
                    "@media screen and (max-width: 599.9px)": {
                      ...(block.xs || {}),
                    },
                  }}
                >
                  <source src={file.node.publicURL} />
                </video>
              );
            }}
          </FileResolver>
        );
      case "img":
        return (
          <ImageBlockComponent block={block} getImagePathFn={getImagePath} />
        );
      case "cards":
        return (
          <CardsBlockComponent block={block} getImagePathFn={getImagePath} />
        );
      case "cta":
        return <CtaBlockComponent block={block} />;
      case "parallax":
        return (
          <ParallaxBlockComponent
            id={index}
            block={block}
            getImagePathFn={getImagePath}
          />
        );
      case "row":
        return (
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            css={{
              "h1,h2,h3,h4,h5,h6": { marginTop: 0 },
              "@media screen and (min-width: 960px)": {
                marginLeft: "-15vw",
                width: "calc(100% + 15vw)",
              },
            }}
          >
            <Grid
              container
              item
              direction="column"
              justifyContent="center"
              alignItems="center"
              md={6}
              sm={12}
              css={{
                "> *": { marginBottom: 24, width: "100%" },
                "@media screen and (min-width: 960px)": {
                  transform: "translate(7vw, 0)",
                },
              }}
              children={renderBlockChildrens(block.left)}
            />
            <Grid
              container
              item
              direction="column"
              justifyContent="center"
              alignItems="center"
              md={6}
              sm={12}
              css={{
                "> *": { marginBottom: 24, width: "100%" },
                "@media screen and (min-width: 960px)": {
                  // transform: 'translate(15vw, 0)',
                  paddingLeft: "15vw",
                },
                "@media screen and (min-width: 2300px)": {
                  transform: "none",
                  paddingLeft: 0,
                },
              }}
              children={renderBlockChildrens(block.right)}
            />
          </Grid>
        );
    }
    // console.log('unsupported block ', block);
    return "---";
  }
};

export default CaseStudy;

const CtaBlockComponent = (props: { block: CtaBlock }) => {
  //todo get accent color
  return (
    <div
      css={{
        width: "100%",
        maxWidth: 570,
        display: "flex",
        flexDirection: "row",
      }}
    >
      <a
        href={props.block.url}
        target="_blank"
        rel="noopener noreferrer"
        css={{ textDecoration: "none" }}
      >
        <div
          css={{
            padding: "16px 24px",
            borderRadius: 16,
            backgroundColor: "#1A59E7",
          }}
        >
          <Typography
            children={props.block.content}
            component="p"
            css={{ width: "auto", color: "white", margin: "0 !important" }}
          />
        </div>
      </a>
    </div>
  );
};

const ImageBlockComponent = (props: {
  getImagePathFn: (str: string) => string;
  block: ImageBlock;
}) => {
  const [dimensions] = useDimensions();
  const isMobile = dimensions.width < 600;

  let block = props.block;
  if (isMobile) {
    block = { ...block, ...(block.xs || {}) };
  }

  const imgpath = props.getImagePathFn(block.content);

  if (block.type === "wide") {
    return (
      <div
        css={{
          marginTop: 32,
          marginBottom: 56,
          position: "relative",
          width: "100%",

          height: 486,
          "@media screen and (max-width: 1279.9px)": { height: 380 },
          "@media screen and (max-width: 959.9px)": { height: 240 },
          "@media screen and (max-width: 599.9px)": { height: 148 },
        }}
      >
        <div
          css={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "calc(-50px - 16.1vw)",
            right: "calc(-50px - 16.1vw)",
            "@media screen and (max-width: 959.9px)": {
              left: -50,
              right: -50,
            },
          }}
        >
          <Image
            filename={imgpath}
            imageProps={{
              // objectFit: "cover",
              css: {
                width: "100%",
                height: "100%",
                // maxWidth: 1920,
                maxWidth: 2048,
                position: "absolute",
                left: 0,
                right: 0,
                margin: "auto",
              },
            }}
          />
        </div>
      </div>
    );
  }
  if (block.type === "raised") {
    return (
      <Image
        filename={imgpath}
        imageProps={{
          css: {
            marginTop: 72,
            marginBottom: 72,
            width: "100%",
            maxWidth: block.width || 792,
            boxShadow:
              "-24px 18px 36px -8px rgba(24, 74, 123, 0.12), inset 0px 0px 36px rgba(255, 255, 255, 0.36)",
            borderRadius: 20,
            "@media screen and (max-width: 599.9px)": {
              marginTop: 32,
              marginBottom: 32,
            },
          },
        }}
      />
    );
  }
  return (
    <Image
      filename={imgpath}
      imageProps={{
        css: {
          marginTop: 32,
          marginBottom: 16,
          width: "100%",
          maxWidth: block.width || (block.height ? undefined : 792), //200
          height: block.height,
        },
      }}
    />
  );
};

const CardsBlockComponent = (props: {
  getImagePathFn: (str: string) => string;
  block: CardsBlock;
}) => {
  const shadowColor = props.block.color || "#e85322";

  const imgCss = {
    width: 108,
    height: 108,
    ...(props.block.withShadow
      ? {
          filter: `drop-shadow(0px 13px 20px ${alpha(shadowColor, 0.15)})`,
        }
      : {}),
    "@media screen and (max-width: 599.9px)": {
      width: 92,
      height: 92,
    },
  };

  return (
    <Grid
      container
      direction="row"
      justifyContent="center"
      css={{
        // maxWidth: 780
        maxWidth: 820,
      }}
      spacing={2}
    >
      {props.block.children?.map((card, index) => {
        return (
          <Grid item sm={4} xs={6} key={index}>
            <Grid
              container
              direction="column"
              alignItems="center"
              css={{
                // width: 244,
                minHeight: 50,
                // height: 250,
                // margin: 12,
                background: props.block.background || "#FCEAE4",
                borderRadius: 40,
                padding: "24px 8px",
                height: "100%",
              }}
            >
              {card.image.startsWith("/") ? (
                <img src={card.image} css={imgCss} />
              ) : (
                <Image
                  filename={props.getImagePathFn(card.image)}
                  imageProps={{ css: imgCss }}
                />
              )}
              <div
                children={card.title}
                css={{
                  fontSize: 24,
                  // fontSize: 26,
                  textAlign: "center",
                  fontWeight: "bold",
                  "@media screen and (max-width: 400px)": {
                    fontSize: 18,
                  },
                  ...(props.block.color
                    ? { color: props.block.color }
                    : {
                        color: "#E5142C",
                        "@supports (background-clip: text) or (-webkit-background-clip: text)": {
                          backgroundColor: "#E5142C",
                          background:
                            "-webkit-linear-gradient(125deg, #F49622, #E5142C)",
                          "-webkit-background-clip": "text",
                          "-webkit-text-fill-color": "transparent",
                        },
                      }),
                }}
              />
            </Grid>
          </Grid>
        );
      })}
    </Grid>
  );
};

const ParallaxBlockComponent = (props: {
  id: number | string;
  getImagePathFn: (str: string) => string;
  block: ParallaxBlock;
}) => {
  const [dimensions] = useDimensions();
  const isMobile = dimensions.width < 600;

  let block = props.block;
  if (isMobile) {
    block = { ...block, ...(block.xs || {}) };
  }

  let blockScale = block.scale || (isMobile ? 0.7 : 1); // (isMobile ? 1.3 : 1);
  const blockLevels = ["liv_2", "liv_1", "liv0", "liv1", "liv2"];

  // if (isMobile) {
  //   if (props.block.xs?.scale) {
  //     blockScale = props.block.xs?.scale;
  //   } else if (!blockScale) {
  //     blockScale = 1.3;
  //   }
  // }

  //? 1.9 scrolls during entire view scroll | 1 only reaching top y START window
  const duration = isMobile ? 1 : 1.9; //1;

  return (
    <MabiloftSmoothScrollInterpolator
      id={props.id}
      duration={duration}
      tweens={blockLevels
        .filter((liv) => liv !== "liv0" && (block as any)[liv])
        .map((liv) => {
          let mult = 1;
          if (liv.includes("_")) {
            mult = -parseInt(liv.replace("liv_", ""));
          } else if (liv !== "liv0") {
            mult = parseInt(liv.replace("liv", ""));
          }

          //? reduce parallax on mobile
          if (isMobile) {
            mult /= 2;
          }

          let from, to;

          if (duration > 1.5) {
            from = { y: 40 * mult };
            to = { y: -60 * mult };

            if (block.intrinsic) {
              from = { y: 80 * mult };
              to = { y: -20 * mult };
            }
          } else {
            from = { y: 50 * mult };
            to = { y: -50 * mult };

            if (block.intrinsic) {
              from = { y: 100 * mult };
              to = { y: 0 * mult };
            }
          }

          return {
            selector: "." + liv,
            from,
            to,
          };
        })}
      // tweens={[
      //   { selector: " .liv_2", from: { x: -200 }, to: { x: 200 } },
      //   { selector: " .liv_1", from: { x: -100 }, to: { x: 100 } },
      //   // { selector: ".liv0", from: { x: -100 }, to: { x: 100 } },
      //   { selector: " .liv1", from: { x: -100 }, to: { x: 100 } },
      //   { selector: " .liv2", from: { x: -100 }, to: { x: 100 } }
      // ]}
    >
      <div
        css={{
          margin: "96px 0",
          position: "relative",
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          // marginLeft: '-10vw',
          // width: "calc(100% + 20vw)",
          // textAlign: "center"
          "@media screen and (max-width: 599.9px)": {
            margin: "48px 0",
          },
        }}
      >
        <Grid
          container
          direction="row"
          justifyContent="center"
          css={{
            // maxWidth: 780
            position: "relative",
            // minHeight: 200
            width: block.width || 820,
            maxWidth: "100%",
            // maxWidth: "97vw"
            // left: -9999,
            // right: -9999,
            // margin: "auto"

            ...(blockScale
              ? {
                  marginTop: blockScale * 10 + "%",
                  marginBottom: blockScale * 10 + "%",
                  transform: blockScale
                    ? "scale(" + blockScale + ")"
                    : undefined,
                }
              : {}),

            // "@media screen and (min-width: 960px)": props.block.scale
            //   ? {
            //       marginTop: props.block.scale * 10 + "%",
            //       marginBottom: props.block.scale * 10 + "%",
            //       transform: props.block.scale
            //         ? "scale(" + props.block.scale + ")"
            //         : undefined
            //     }
            //   : undefined
          }}
        >
          {blockLevels.map((liv, index) => {
            const blockLiv = (block as any)[liv];

            if (!blockLiv) return null;

            //TODO impl mult parallax

            // if (isMobile) return <div key={block[liv]} children={block[liv]} />;

            return (
              <Image
                key={liv + "_" + blockLiv}
                filename={props.getImagePathFn(blockLiv)}
                imageProps={{
                  className: liv,
                  css: {
                    width: "100%",
                    ...(liv === "liv0"
                      ? {}
                      : {
                          position: "absolute !important",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                        }),
                  },
                }}
              />
            );
          })}
        </Grid>
      </div>
    </MabiloftSmoothScrollInterpolator>
  );
};
